import React, { useContext, useState } from "react";
import { Link } from "react-router-dom";
import { useIntl } from "react-intl";
import { useParams } from "react-router";
import { ArrowLeft, CheckCircle, Clock, CreditCard, Download, Info, MapPin, XCircle } from "lucide-react";
import fileDownload from "js-file-download";
import { Context as AuthContext } from "context/Auth";
import { downloadBookingSalesSuccession, useBooking } from "hooks/booking";
import { downloadInvoice } from "hooks/invoice";
import { Button } from "components/ui/button";
import StarRating from "components/ui/star-rating";
import { Loader } from "ui/Loader";
import { cn } from "lib/utils";
import { ROUTES } from "routes";
import { BookingStatus } from "types/booking";
import { Role } from "types/user";
import {
    formatDuration,
    formatEuroAmount,
    formatServiceHistoryDate,
    formatServiceHistoryTime,
    getBookingAmount,
    getFullName,
    getHourlyRateAmount,
    getClientServiceTotalAfterTaxCredit,
    getClientServiceTotalAmount,
    getManagementFeesAmount,
    getProviderBookingAmount,
    getSocialContributionsAmount,
    getTaxCreditAmount,
    isCesuPlusActive,
    isSupportedRole,
} from "../format";
import { getStatusBadgeClassName, getStatusLabelId, isAutoValidatableStatus } from "../status";
import { useServiceTitle } from "../useServiceTitle";

type DetailRowProps = {
    label: string;
    value: string;
    valueClassName?: string;
    showCreditCardIcon?: boolean;
    hint?: string;
};

const TotalAmountDisplay: React.FC<{
    amount: string;
    className?: string;
    iconClassName?: string;
    showCreditCardIcon?: boolean;
}> = ({ amount, className, iconClassName = "h-4 w-4", showCreditCardIcon = false }) => (
    <span className={cn("inline-flex items-center gap-1.5", className)}>
        {showCreditCardIcon ? (
            <CreditCard className={cn(iconClassName, "shrink-0 text-slate-500")} aria-hidden />
        ) : null}
        {amount}
    </span>
);

const DetailRow: React.FC<DetailRowProps> = ({ label, value, valueClassName, showCreditCardIcon, hint }) => (
    <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
        <dt className="text-sm text-slate-500">
            {label}
            {hint ? <span className="mt-0.5 block text-xs text-slate-400">{hint}</span> : null}
        </dt>
        <dd className={cn("text-sm font-medium text-slate-900", valueClassName)}>
            <TotalAmountDisplay amount={value} showCreditCardIcon={showCreditCardIcon} />
        </dd>
    </div>
);

const ServiceHistoryDetails: React.FC = () => {
    const intl = useIntl();
    const { id } = useParams();
    const { currentRole } = useContext(AuthContext);
    const supportedRole = isSupportedRole(currentRole) ? currentRole : null;
    const { data: booking, isLoading, isError } = useBooking(id);
    const serviceTitle = useServiceTitle(booking);
    const [isDownloadingSalesSuccession, setIsDownloadingSalesSuccession] = useState(false);
    const [isDownloadingInvoice, setIsDownloadingInvoice] = useState(false);

    const handleDownloadSalesSuccession = async () => {
        if (!booking || isDownloadingSalesSuccession) {
            return;
        }

        setIsDownloadingSalesSuccession(true);
        try {
            const response = await downloadBookingSalesSuccession({ booking_id: booking.id });
            fileDownload(response.content, response.name);
        } finally {
            setIsDownloadingSalesSuccession(false);
        }
    };

    const handleDownloadInvoice = async () => {
        if (!booking || isDownloadingInvoice) {
            return;
        }

        setIsDownloadingInvoice(true);
        try {
            const response = await downloadInvoice(booking.id);
            fileDownload(response.content, response.name);
        } finally {
            setIsDownloadingInvoice(false);
        }
    };

    if (isLoading) {
        return <Loader />;
    }

    if (isError || !booking || !supportedRole) {
        return (
            <div className="rounded-2xl border border-slate-200 bg-white p-6">
                <Link
                    to={ROUTES.SERVICE_HISTORY}
                    className="mb-6 inline-flex items-center gap-2 text-sm font-medium text-slate-600 transition-colors hover:text-primary"
                >
                    <ArrowLeft className="h-4 w-4" strokeWidth={2.25} />
                    {intl.formatMessage({ id: "profile.service_history.details.back" })}
                </Link>
                <p className="text-sm text-destructive">
                    {intl.formatMessage({ id: "profile.service_history.details.error" })}
                </p>
            </div>
        );
    }

    const isClosed = booking.status === BookingStatus.CLOSED;
    const isCancelled = booking.status === BookingStatus.CANCELLED;
    const interlocutor = supportedRole === Role.PROVIDER ? booking.client : booking.provider;
    const interlocutorLabelId = supportedRole === Role.PROVIDER
        ? "profile.service_history.details.client"
        : "profile.service_history.details.provider";
    const providerNote = booking.notes?.provider;
    const statusLabelId = getStatusLabelId(booking.status);
    const hourlyRateAmount = getHourlyRateAmount(booking);
    const salaryAmount = getProviderBookingAmount(booking);
    const socialContributionsAmount = getSocialContributionsAmount(booking);
    const managementFeesAmount = getManagementFeesAmount(booking);
    const serviceTotalAmount = formatEuroAmount(getClientServiceTotalAmount(booking));
    const serviceTotalAfterTaxCreditAmount = formatEuroAmount(getClientServiceTotalAfterTaxCredit(booking));
    const taxCreditAmount = formatEuroAmount(getTaxCreditAmount(booking));
    const totalAmount = formatEuroAmount(getBookingAmount(booking, supportedRole));
    const totalLabelId = supportedRole === Role.PROVIDER
        ? "profile.service_history.details.total_received"
        : "profile.service_history.details.total";
    const providerTotalClassName = supportedRole === Role.PROVIDER ? "text-emerald-600" : undefined;
    const showCreditCardIcon = supportedRole === Role.CLIENT;
    const cesuPlusActive = isCesuPlusActive(booking);
    const cesuPlusLabel = intl.formatMessage({
        id: cesuPlusActive
            ? "profile.service_history.details.yes"
            : "profile.service_history.details.no",
    });
    const StatusIcon = isClosed ? CheckCircle : (isCancelled ? XCircle : Clock);
    const address = booking.client_address || interlocutor.address;
    const showProviderSalesSuccessionDownload = supportedRole === Role.PROVIDER && isClosed;
    const showClientInvoiceDownload = supportedRole === Role.CLIENT && isClosed && booking.invoices_count > 0;
    const showProviderSalesSuccessionPending = supportedRole === Role.PROVIDER && !isClosed;
    const showClientInvoicePending = supportedRole === Role.CLIENT && !isClosed && !isCancelled;
    const showDownloadSection = showProviderSalesSuccessionDownload
        || showClientInvoiceDownload
        || showProviderSalesSuccessionPending
        || showClientInvoicePending;
    const showAutoValidateNotice = isAutoValidatableStatus(booking.status);
    const salaryHint = intl.formatMessage(
        { id: "profile.service_history.details.salary_hint" },
        {
            rate: formatEuroAmount(hourlyRateAmount),
            duration: formatDuration(booking.nb_hours),
        },
    );
    const salaryRow = (
        <DetailRow
            label={intl.formatMessage({ id: "profile.service_history.details.salary" })}
            hint={salaryHint}
            value={formatEuroAmount(salaryAmount)}
        />
    );

    return (
        <div className="space-y-6 mx-16 my-12">
            <Link
                to={ROUTES.SERVICE_HISTORY}
                className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 transition-colors hover:text-primary"
            >
                <ArrowLeft className="h-4 w-4" strokeWidth={2.25} />
                {intl.formatMessage({ id: "profile.service_history.details.back" })}
            </Link>

            <article
                data-testid="service-history-details"
                className="rounded-2xl border border-slate-200 bg-white p-6"
            >
                <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                    <div className="flex min-w-0 items-start gap-4">
                        <div
                            className={cn(
                                "flex h-12 w-12 shrink-0 items-center justify-center rounded-[10px]",
                                isClosed ? "bg-emerald-50 text-emerald-600" : (isCancelled ? "bg-red-50 text-red-600" : "bg-violet-100 text-indigo-600"),
                            )}
                        >
                            <StatusIcon className="h-5 w-5" aria-hidden />
                        </div>
                        <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                                <h1 className="text-xl font-bold text-slate-900">{serviceTitle}</h1>
                                {statusLabelId ? (
                                    <span
                                        className={cn(
                                            "inline-flex h-6 items-center justify-center rounded-[10px] border px-3 text-xs font-medium leading-4",
                                            getStatusBadgeClassName(booking.status),
                                        )}
                                    >
                                        {intl.formatMessage({ id: statusLabelId })}
                                    </span>
                                ) : null}
                                {isClosed ? (
                                    <span className="inline-flex h-6 items-center justify-center rounded-[10px] border border-emerald-200 bg-emerald-50 px-3 text-xs font-medium leading-4 text-emerald-700">
                                        {intl.formatMessage({ id: "profile.service_history.details.closed" })}
                                    </span>
                                ) : null}
                            </div>
                            <p className="mt-1 text-sm text-slate-500">
                                {intl.formatMessage({ id: "profile.service_history.details.reference" }, {
                                    code: booking.code,
                                })}
                            </p>
                        </div>
                    </div>
                    <div className="text-right">
                        <TotalAmountDisplay
                            amount={totalAmount}
                            showCreditCardIcon={showCreditCardIcon}
                            className={cn("text-2xl font-extrabold tabular-nums", providerTotalClassName ?? "text-slate-900")}
                            iconClassName="h-5 w-5"
                        />
                        <div className="text-xs font-medium text-slate-500">
                            {intl.formatMessage({ id: totalLabelId })}
                        </div>
                    </div>
                </div>

                {showAutoValidateNotice ? (
                    <p
                        data-testid="service-history-details-auto-validate-notice"
                        className="mb-6 flex items-start gap-2 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm leading-5 text-slate-400"
                    >
                        <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                        <span>
                            {intl.formatMessage({ id: "profile.service_history.auto_validate_notice" })}
                        </span>
                    </p>
                ) : null}

                <section className="mb-6 rounded-xl bg-slate-50 p-4">
                    <h2 className="mb-4 text-sm font-semibold text-slate-900">
                        {intl.formatMessage({ id: "profile.service_history.details.general_info" })}
                    </h2>
                    <dl className="flex flex-col gap-3">
                        <DetailRow
                            label={intl.formatMessage({ id: interlocutorLabelId })}
                            value={getFullName(interlocutor)}
                        />
                        <DetailRow
                            label={intl.formatMessage({ id: "profile.service_history.details.date" })}
                            value={
                                booking.start_time
                                    ? intl.formatMessage(
                                          { id: "profile.service_history.date_time" },
                                          {
                                              date: formatServiceHistoryDate(booking.date),
                                              time: formatServiceHistoryTime(booking.start_time),
                                          },
                                      )
                                    : formatServiceHistoryDate(booking.date)
                            }
                        />
                        <DetailRow
                            label={intl.formatMessage({ id: "profile.service_history.details.duration" })}
                            value={formatDuration(booking.nb_hours)}
                        />
                        {address ? (
                            <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
                                <dt className="text-sm text-slate-500">
                                    {intl.formatMessage({ id: "profile.service_history.details.address" })}
                                </dt>
                                <dd className="flex items-start gap-1.5 text-sm font-medium text-slate-900 sm:max-w-sm sm:text-right">
                                    <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" aria-hidden />
                                    <span>{address}</span>
                                </dd>
                            </div>
                        ) : null}
                    </dl>
                </section>

                <section className="mb-6 rounded-xl bg-slate-50 p-4">
                    <h2 className="mb-4 text-sm font-semibold text-slate-900">
                        {intl.formatMessage({ id: "profile.service_history.details.billing" })}
                    </h2>
                    {supportedRole === Role.CLIENT ? (
                        <div className="flex flex-col gap-5">
                            <div data-testid="service-history-paid-breakdown">
                                <div className="mb-3 flex items-center justify-between gap-3">
                                    <h3 className="text-sm font-semibold text-slate-900">
                                        {intl.formatMessage({ id: "profile.service_history.details.total" })}
                                    </h3>
                                    <TotalAmountDisplay
                                        amount={totalAmount}
                                        showCreditCardIcon
                                        className="text-sm font-semibold text-slate-900"
                                    />
                                </div>
                                <dl className="flex flex-col gap-3 border-l-2 border-slate-200 pl-3">
                                    {cesuPlusActive ? null : salaryRow}
                                    <DetailRow
                                        label={intl.formatMessage({ id: "profile.service_history.details.twoghether_commission" })}
                                        value={formatEuroAmount(managementFeesAmount)}
                                    />
                                </dl>
                            </div>
                            <div
                                data-testid="service-history-urssaf-breakdown"
                                className="border-t border-slate-200 pt-4"
                            >
                                <h3 className="mb-3 text-sm font-semibold text-slate-900">
                                    {intl.formatMessage({ id: "profile.service_history.details.urssaf_tax_credit" })}
                                </h3>
                                <dl className="flex flex-col gap-3 border-l-2 border-slate-200 pl-3">
                                    <DetailRow
                                        label={intl.formatMessage({ id: "profile.service_history.details.cesu_plus" })}
                                        value={cesuPlusLabel}
                                    />
                                    {cesuPlusActive ? salaryRow : null}
                                    <DetailRow
                                        label={intl.formatMessage({ id: "profile.service_history.details.social_contributions" })}
                                        hint={intl.formatMessage({ id: "profile.service_history.details.social_contributions_hint" })}
                                        value={formatEuroAmount(socialContributionsAmount)}
                                    />
                                    <DetailRow
                                        label={intl.formatMessage({ id: "profile.service_history.details.tax_credit" })}
                                        hint={intl.formatMessage({ id: "profile.service_history.details.tax_credit_hint" })}
                                        value={taxCreditAmount}
                                        valueClassName="text-emerald-600"
                                    />
                                </dl>
                            </div>
                            <div className="border-t border-slate-200 pt-4">
                                <dl className="flex flex-col gap-3">
                                    <DetailRow
                                        label={intl.formatMessage({ id: "profile.service_history.details.service_total" })}
                                        value={serviceTotalAmount}
                                    />
                                    <DetailRow
                                        label={intl.formatMessage({ id: "profile.service_history.details.service_total_after_tax_credit" })}
                                        value={serviceTotalAfterTaxCreditAmount}
                                    />
                                </dl>
                            </div>
                        </div>
                    ) : (
                        <dl className="flex flex-col gap-3">
                            <DetailRow
                                label={intl.formatMessage({ id: "profile.service_history.details.cesu_plus" })}
                                value={cesuPlusLabel}
                            />
                            <DetailRow
                                label={intl.formatMessage({ id: "profile.service_history.details.hourly_rate" })}
                                value={formatEuroAmount(hourlyRateAmount)}
                            />
                            <DetailRow
                                label={intl.formatMessage({ id: totalLabelId })}
                                value={totalAmount}
                                valueClassName={providerTotalClassName}
                            />
                        </dl>
                    )}
                </section>

                {booking.booking_comment ? (
                    <section className="mb-6 rounded-xl bg-slate-50 p-4">
                        <h2 className="mb-2 text-sm font-semibold text-slate-900">
                            {intl.formatMessage({ id: "profile.service_history.details.comment" })}
                        </h2>
                        <p className="text-sm text-slate-700">{booking.booking_comment}</p>
                    </section>
                ) : null}

                {isClosed && providerNote ? (
                    <section className="rounded-xl bg-slate-50 p-4">
                        <h2 className="mb-3 text-sm font-semibold text-slate-900">
                            {intl.formatMessage({ id: "profile.service_history.details.rating" })}
                        </h2>
                        <StarRating
                            value={providerNote.note}
                            starClassName="h-5 w-5"
                            emptyStarClassName="fill-transparent stroke-slate-300"
                            filledStarClassName="fill-amber-500 stroke-amber-500"
                        />
                        {providerNote.comment ? (
                            <p className="text-sm text-slate-700">{providerNote.comment}</p>
                        ) : null}
                    </section>
                ) : null}

                {showDownloadSection ? (
                    <section className="border-t border-slate-100 pt-6">
                        {isClosed ? (
                            supportedRole === Role.PROVIDER ? (
                                <Button
                                    type="button"
                                    variant="outline"
                                    className="rounded-xl"
                                    disabled={isDownloadingSalesSuccession}
                                    onClick={handleDownloadSalesSuccession}
                                >
                                    <Download className="h-4 w-4" aria-hidden />
                                    {intl.formatMessage({ id: "profile.service_history.details.download_sales_succession" })}
                                </Button>
                            ) : (
                                <Button
                                    type="button"
                                    variant="outline"
                                    className="rounded-xl"
                                    disabled={isDownloadingInvoice}
                                    onClick={handleDownloadInvoice}
                                >
                                    <Download className="h-4 w-4" aria-hidden />
                                    {intl.formatMessage({ id: "profile.service_history.details.download_invoice_summary" })}
                                </Button>
                            )
                        ) : (
                            <p className="text-sm italic text-slate-500">
                                {intl.formatMessage({
                                    id: supportedRole === Role.PROVIDER
                                        ? "profile.service_history.details.sales_succession_pending"
                                        : "profile.service_history.details.invoice_summary_pending",
                                })}
                            </p>
                        )}
                    </section>
                ) : null}
            </article>
        </div>
    );
};

export default ServiceHistoryDetails;
